import { Flex, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import {
  AsyncDisplay,
  Chip,
  FormSummaryItem,
  Heading,
  Skeleton,
  Text,
  Well,
} from '@feathr/components';
import GeoFiltersList from '@feathr/extender/components/Targetings/Locations/GeoFiltersList';
import { getGeoFilterIds } from '@feathr/extender/components/Targetings/Targetings.utils';
import { useStore } from '@feathr/extender/state';

import { CampaignContext } from '../CampaignEditPage/CampaignEditPage.context';

import * as styles from './CampaignSummary.css';

interface IProps {
  campaign: Campaign;
  validationErrors?: {
    filters?: string[];
  };
}

function filterAgeDemographicTargetings(trgt: Targeting): boolean {
  return (
    trgt.get('kind') === 'age_demographic' && !trgt.get('is_archived') && !!trgt.get('target_data')
  );
}

function filterInterestTargetings(trgt: Targeting): boolean {
  return trgt.get('kind') === 'interest' && !trgt.get('is_archived') && !!trgt.get('target_data');
}

const FiltersSummary = observer(({ validationErrors, campaign }: IProps): JSX.Element => {
  const { t } = useTranslation();
  const { Targetables } = useStore();
  const { targetings } = useContext(CampaignContext);
  const theme = useMantineTheme();
  const { isOmniCampaign, isOmniReachCampaign } = campaign;
  const none = t('(None)');

  const [geoFilterIds, setGeoFilterIds] = useState<{
    included: string[];
    excluded: string[];
    geosetName?: string;
  }>({ included: [], excluded: [] });
  const [isLoadingGeoFilters, setIsLoadingGeoFilters] = useState(false);

  useEffect(() => {
    if (!targetings || targetings.isPending) {
      return;
    }

    const loadGeoFilterIds = async (): Promise<void> => {
      setIsLoadingGeoFilters(true);
      try {
        const ids = await getGeoFilterIds(targetings.models, Targetables);
        setGeoFilterIds(ids);
      } catch (error) {
        setGeoFilterIds({ included: [], excluded: [] });
      } finally {
        setIsLoadingGeoFilters(false);
      }
    };

    loadGeoFilterIds();
  }, [targetings, targetings?.isPending, Targetables]);
  const ageDemographicFilteredTargetings =
    targetings?.models.filter(filterAgeDemographicTargetings) ?? [];
  const interestFilteredTargetings = targetings?.models.filter(filterInterestTargetings) ?? [];

  const hasTargetings =
    geoFilterIds.included.length > 0 ||
    geoFilterIds.excluded.length > 0 ||
    ageDemographicFilteredTargetings.length > 0 ||
    interestFilteredTargetings.length > 0;

  const ageDemographicTargetingListItems = ageDemographicFilteredTargetings.map((ad) => {
    return (
      <Chip className={styles.chipFix} key={ad.id}>
        {ad.get('name')} years
      </Chip>
    );
  });

  const interestTargetingListItems = interestFilteredTargetings.map((interest) => {
    return (
      <Chip className={styles.chipFix} key={interest.id}>
        {interest.get('name')}
      </Chip>
    );
  });

  const geoFiltersWell = (
    <Well data-name={'filters_locations'} layout={'vertical'}>
      <Stack gap={theme.spacing[2]}>
        <div>
          <Heading order={5} variant={'h5'}>
            {geoFilterIds.geosetName ? t('Geofilter Set Name') : t('Locations')}
          </Heading>
          {geoFilterIds.geosetName && (
            <Text.DefaultRegular>{geoFilterIds.geosetName}</Text.DefaultRegular>
          )}
        </div>
        <AsyncDisplay isLoading={isLoadingGeoFilters || targetings?.isPending}>
          {geoFilterIds.included.length > 0 || geoFilterIds.excluded.length > 0 ? (
            <GeoFiltersList
              excludedGeoFilterIds={geoFilterIds.excluded}
              includedGeoFilterIds={geoFilterIds.included}
            />
          ) : (
            <p>{none}</p>
          )}
        </AsyncDisplay>
      </Stack>
    </Well>
  );

  const omniCampaignFilters = (
    <Stack gap={theme.spacing[2]}>
      {geoFiltersWell}
      <Well data-name={'filters_age_demographics'} layout={'vertical'}>
        <Stack gap={theme.spacing[1]}>
          <Heading order={5} variant={'h5'}>
            {t('Age Demographics')}
          </Heading>
          <Flex gap={theme.spacing['2']} wrap={'wrap'}>
            {ageDemographicTargetingListItems.length > 0 ? (
              ageDemographicTargetingListItems
            ) : (
              <p>{none}</p>
            )}
          </Flex>
        </Stack>
      </Well>
      <Well data-name={'filters_interests'} layout={'vertical'}>
        <Stack gap={theme.spacing[1]}>
          <Heading order={5} variant={'h5'}>
            {t('Interests')}
          </Heading>
          <Flex gap={theme.spacing['2']} wrap={'wrap'}>
            {interestTargetingListItems.length > 0 ? interestTargetingListItems : <p>{none}</p>}
          </Flex>
        </Stack>
      </Well>
    </Stack>
  );

  const locationFilters = geoFiltersWell;

  const formSummary = isOmniCampaign ? omniCampaignFilters : locationFilters;

  return (
    <FormSummaryItem
      errors={validationErrors?.filters}
      label={isOmniReachCampaign ? t('Audience') : t('Filters')}
      name={'summary_filters'}
      value={
        targetings?.isPending ? (
          <ol>
            <li>
              <Skeleton width={100} />
            </li>
          </ol>
        ) : hasTargetings ? (
          formSummary
        ) : (
          none
        )
      }
    />
  );
});

FiltersSummary.displayName = 'FiltersSummary';

export default FiltersSummary;
